import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';
import { getMiBoletaEstudiante } from '../../api/calificaciones';
import { generateBoletaCalificacionesPDF } from '../../utils/generateBoletaCalificacionesPDF';

// Calcula la calificacion final con la misma regla usada en Estudiantes.
const calcularFinal = (p1, p2, p3) => {
  if (p1 == null || p2 == null || p3 == null) return '';
  const total = (Number(p1) + Number(p2) + Number(p3)) / 3;
  if (total === 0) return 0;
  if (total < 6) return 5;
  if (total < 7) return 6;
  if (total < 7.5) return 7;
  if (total < 8.5) return 8;
  if (total < 9.5) return 9;
  return 10;
};

const getFinalMostrada = (materia) => (
  materia?.final != null
    ? materia.final
    : calcularFinal(materia?.parcial_1, materia?.parcial_2, materia?.parcial_3)
);

const formatCal = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  const n = Number(val);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/**
 * Componente de Calificaciones del Estudiante
 * 
 * ESTADO: Pendiente de implementación
 * TODO: Conectar con la tabla de calificaciones cuando esté disponible
 * 
 * @param {Object} studentData - Datos del estudiante desde el backend
 */
function Calificaciones({ studentData }) {
  const [boleta, setBoleta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCicloId, setSelectedCicloId] = useState('');

  const ciclosDisponibles = useMemo(() => boleta?.ciclos || [], [boleta]);

  const ciclosFiltrados = useMemo(() => {
    if (!selectedCicloId) return ciclosDisponibles;
    return ciclosDisponibles.filter(
      (ciclo) => String(ciclo?.id_ciclo_escolar) === String(selectedCicloId)
    );
  }, [ciclosDisponibles, selectedCicloId]);

  const handleGenerarPDF = useCallback(() => {
    const totalMaterias = ciclosFiltrados.reduce(
      (acc, ciclo) => acc + ((ciclo?.materias || []).length),
      0,
    );
    if (!boleta || totalMaterias === 0) return;

    generateBoletaCalificacionesPDF({
      encabezado: boleta.encabezado,
      ciclos: ciclosFiltrados,
      descargar: true,
    });
  }, [boleta, ciclosFiltrados]);

  const cargarBoleta = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError('');
      const data = await getMiBoletaEstudiante();
      setBoleta(data || null);
    } catch (err) {
      console.error('[Portal][Calificaciones] Error cargando boleta:', err);
      setError('No fue posible cargar las calificaciones en este momento.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (!isMounted) return;
      await cargarBoleta({ silent: false });
    };

    init();

    const intervalId = setInterval(() => {
      if (isMounted) {
        cargarBoleta({ silent: true });
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [cargarBoleta]);

  useEffect(() => {
    const handleGruposUpdated = () => {
      cargarBoleta({ silent: true });
    };

    window.addEventListener('estudiantes-grupos:updated', handleGruposUpdated);
    return () => {
      window.removeEventListener('estudiantes-grupos:updated', handleGruposUpdated);
    };
  }, [cargarBoleta]);

  useEffect(() => {
    if (!selectedCicloId) return;

    const exists = ciclosDisponibles.some(
      (ciclo) => String(ciclo?.id_ciclo_escolar) === String(selectedCicloId)
    );

    if (!exists) {
      setSelectedCicloId('');
    }
  }, [ciclosDisponibles, selectedCicloId]);

  const calificacionesData = useMemo(() => {
    const ciclos = ciclosFiltrados;
    const rows = [];

    ciclos.forEach((ciclo) => {
      (ciclo?.materias || []).forEach((materia) => {
        const parcial1 = materia?.parcial_1;
        const parcial2 = materia?.parcial_2;
        const parcial3 = materia?.parcial_3;
        const final = getFinalMostrada(materia);
        const extraordinario = materia?.extraordinario;

        rows.push({
          id: `${ciclo?.id_ciclo_escolar || '0'}-${materia?.id_estudiante_grupo || materia?.id_grupo || materia?.id_materia}`,
          ciclo: ciclo?.nombre_ciclo || 'Sin ciclo',
          grupo: materia?.grupo_codigo || 'N/A',
          materia: materia?.materia_nombre || 'N/A',
          docente: materia?.asesor_nombre || 'N/A',
          parcial1,
          parcial2,
          parcial3,
          final,
          extraordinario,
        });
      });
    });

    return rows;
  }, [ciclosFiltrados]);

  const promedioGeneral = useMemo(() => {
    const conFinal = calificacionesData.filter((item) => item.final !== null && item.final !== undefined && item.final !== '');
    if (conFinal.length === 0) return null;
    const suma = conFinal.reduce((acc, item) => acc + Number(item.final || 0), 0);
    return (suma / conFinal.length).toFixed(1);
  }, [calificacionesData]);

  if (loading) {
    return (
      <div className={styles.calificacionesContainer}>
        <div className={styles.calificacionesHeader}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>📊</span>
            <h3 className={styles.sectionTitle}>Calificaciones</h3>
          </div>
        </div>
        <div className={styles.pendingModuleContainer}>
          <div className={styles.pendingModuleTitle}>Cargando calificaciones...</div>
          <p className={styles.pendingModuleText}>Obteniendo información académica del estudiante.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.calificacionesContainer}>
        <div className={styles.calificacionesHeader}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>📊</span>
            <h3 className={styles.sectionTitle}>Calificaciones</h3>
          </div>
        </div>
        <div className={styles.pendingModuleContainer}>
          <h4 className={styles.pendingModuleTitle}>No se pudieron cargar las calificaciones</h4>
          <p className={styles.pendingModuleText}>{error}</p>
          <button
            onClick={() => cargarBoleta({ silent: false })}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #002f6c',
              background: '#002f6c',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!calificacionesData || calificacionesData.length === 0) {
    return (
      <div className={styles.calificacionesContainer}>
        <div className={styles.calificacionesHeader}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>📊</span>
            <h3 className={styles.sectionTitle}>Calificaciones</h3>
          </div>
        </div>
        
        <div className={styles.pendingModuleContainer}>
          <div className={styles.pendingModuleIcon}>📝</div>
          <h4 className={styles.pendingModuleTitle}>Sin calificaciones registradas</h4>
          <p className={styles.pendingModuleText}>
            Aún no hay calificaciones capturadas para este estudiante.
          </p>
          <div className={styles.pendingModuleInfo}>
            <strong>Estudiante:</strong> {studentData?.nombre || 'No disponible'}<br/>
            <strong>Programa:</strong> {studentData?.programa?.nombre || 'No disponible'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.calificacionesContainer}>
      <div className={styles.calificacionesHeader}>
        <div className={`${styles.sectionHeader} ${styles.calificacionesTitleHeader}`}>
          <span className={styles.sectionIcon}>📊</span>
          <h3 className={styles.sectionTitle}>Calificaciones</h3>
        </div>
      </div>

      <div className={styles.periodSelector}>
        <div className={styles.calificacionesActionsGroup}>
          <div className={styles.calificacionesFilterGroup}>
            <label htmlFor="calificaciones-ciclo" className={styles.calificacionesFilterLabel}>
              CICLO ESCOLAR
            </label>
            <select
              id="calificaciones-ciclo"
              className={styles.calificacionesFilterSelect}
              value={selectedCicloId}
              onChange={(e) => setSelectedCicloId(e.target.value)}
            >
              <option value="">Todos los ciclos</option>
              {ciclosDisponibles.map((ciclo) => (
                <option key={ciclo?.id_ciclo_escolar || ciclo?.nombre_ciclo} value={ciclo?.id_ciclo_escolar || ''}>
                  {ciclo?.nombre_ciclo || 'Sin ciclo'}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className={styles.calificacionesPdfButton}
            onClick={handleGenerarPDF}
            disabled={calificacionesData.length === 0}
            title="Descargar boleta en PDF"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            <span>PDF</span>
          </button>
        </div>
        <div className={styles.promedioGeneralBadge}>
          <span className={styles.promedioLabel}>Promedio General</span>
          <span className={styles.promedioValue}>{promedioGeneral || '—'}</span>
        </div>
      </div>

      <div className={styles.calificacionesTableContainer}>
        <table className={styles.calificacionesTable}>
          <thead>
            <tr>
              <th>GRUPO</th>
              <th>MATERIA</th>
              <th>CICLO ESCOLAR</th>
              <th>DOCENTE</th>
              <th>P1</th>
              <th>P2</th>
              <th>P3</th>
              <th>FINAL</th>
              <th>EXTRA.</th>
            </tr>
          </thead>
          <tbody>
            {calificacionesData.map((row) => (
              <tr key={row.id}>
                <td className={styles.grupoCell}>{row.grupo}</td>
                <td className={styles.materiaCell}>{row.materia}</td>
                <td>{row.ciclo}</td>
                <td>{row.docente}</td>
                <td className={styles.gradeCell}>
                  <span className={styles.gradeValue}>{formatCal(row.parcial1)}</span>
                </td>
                <td className={styles.gradeCell}>
                  <span className={styles.gradeValue}>{formatCal(row.parcial2)}</span>
                </td>
                <td className={styles.gradeCell}>
                  <span className={styles.gradeValue}>{formatCal(row.parcial3)}</span>
                </td>
                <td className={styles.gradeCellFinal}>
                  <span className={styles.gradeValueFinal}>{formatCal(row.final)}</span>
                </td>
                <td className={styles.gradeCell}>
                  <span className={styles.gradeValue}>{formatCal(row.extraordinario)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Información adicional */}
      <div className={styles.calificacionesFooter}>
        <div className={styles.infoBox}>
          <span className={styles.infoIcon}>ℹ️</span>
          <p className={styles.infoText}>
            Las calificaciones se sincronizan automáticamente cada 30 segundos con el registro docente.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Calificaciones;
